import React, { useState, useEffect } from 'react';
import { Search, Clock, Users, X, Share2, Send, Globe, Lock } from 'lucide-react';
import { Recipe } from '../types';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Button } from './ui/button';
import { formatTime, generateRecipeCard } from '@/lib/utils';

interface RecipeListProps {
  recipes: Recipe[];
}

export default function RecipeList({ recipes }: RecipeListProps) {
  const [searchIngredients, setSearchIngredients] = useState<string[]>([]);
  const [currentIngredient, setCurrentIngredient] = useState('');
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>(recipes);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [sharing, setSharing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [sendingRecipe, setSendingRecipe] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  useEffect(() => {
    if (searchIngredients.length === 0) {
      setFilteredRecipes(recipes);
    } else {
      const filtered = recipes.filter((recipe) =>
        searchIngredients.every((ingredient) =>
          recipe.ingredients.some((ri) =>
            ri.name.toLowerCase().includes(ingredient.toLowerCase())
          )
        )
      );
      setFilteredRecipes(filtered);
    }
  }, [recipes, searchIngredients]);

  const handleAddIngredient = () => {
    if (currentIngredient && !searchIngredients.includes(currentIngredient)) {
      setSearchIngredients([...searchIngredients, currentIngredient]);
      setCurrentIngredient('');
    }
  };

  const handleSearchUsers = async (query: string) => {
    setSearchUser(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(profiles || []);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    }
  };

  const handleSendRecipe = async (recipe: Recipe, recipientId: string) => {
    if (!recipe || !recipientId) return;

    setSendingRecipe(true);
    try {
      const { data: newRecipe, error: recipeError } = await supabase
        .from('recipes')
        .insert({
          name: recipe.name,
          ingredients: recipe.ingredients,
          instructions: recipe.instructions,
          prep_time: recipe.prepTime,
          cook_time: recipe.cookTime,
          servings: recipe.servings,
          image_url: recipe.imageUrl,
          source: recipe.source,
          tags: recipe.tags,
          is_public: false,
          user_id: recipientId
        })
        .select()
        .single();

      if (recipeError) throw recipeError;

      toast.success('Recipe shared successfully!');
      setShowShareModal(false);
      setSelectedUser(null);
      setSearchUser('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error sharing recipe:', error);
      toast.error('Failed to share recipe');
    } finally {
      setSendingRecipe(false);
    }
  };

  const handleExternalShare = async (recipe: Recipe, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    setSharing(true);
    try {
      const recipeImage = await generateRecipeCard(recipe);
      const file = new File([recipeImage], `${recipe.name.toLowerCase().replace(/\s+/g, '-')}-recipe.png`, {
        type: 'image/png'
      });

      if (navigator.share && navigator.canShare) {
        const shareData = {
          title: recipe.name,
          text: `Check out this recipe for ${recipe.name}!`,
          files: [file]
        };

        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            toast.success('Recipe shared successfully!');
            return;
          } catch (shareError: any) {
            if (shareError.name !== 'AbortError') {
              console.error('Share failed:', shareError);
            }
          }
        }
      }

      // Fallback to download if sharing not available
      const imageUrl = URL.createObjectURL(recipeImage);
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${recipe.name.toLowerCase().replace(/\s+/g, '-')}-recipe.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(imageUrl);
      toast.success('Recipe image downloaded!');
    } catch (error) {
      console.error('Error sharing recipe:', error);
      toast.error('Failed to share recipe');
    } finally {
      setSharing(false);
    }
  };

  const toggleVisibility = async (recipe: Recipe) => {
    setUpdatingVisibility(true);
    try {
      const { error } = await supabase
        .from('recipes')
        .update({ is_public: !recipe.isPublic })
        .eq('id', recipe.id);

      if (error) throw error;

      // Update local state
      const updatedRecipes = recipes.map(r => 
        r.id === recipe.id ? { ...r, isPublic: !r.isPublic } : r
      );
      setFilteredRecipes(updatedRecipes);
      if (selectedRecipe) {
        setSelectedRecipe({ ...selectedRecipe, isPublic: !selectedRecipe.isPublic });
      }

      toast.success(`Recipe is now ${!recipe.isPublic ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error updating recipe visibility:', error);
      toast.error('Failed to update recipe visibility');
    } finally {
      setUpdatingVisibility(false);
    }
  };

  if (!recipes || recipes.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Recipes Yet</h3>
          <p className="text-gray-500 mb-6">
            Start building your collection by adding your first recipe
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="max-w-xl">
          <div className="relative">
            <input
              type="text"
              value={currentIngredient}
              onChange={(e) => setCurrentIngredient(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddIngredient()}
              placeholder="Search recipes by ingredients..."
              className="w-full px-4 py-3 pl-12 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>
        {searchIngredients.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {searchIngredients.map((ingredient) => (
              <span
                key={ingredient}
                className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm flex items-center"
              >
                {ingredient}
                <button
                  onClick={() =>
                    setSearchIngredients(searchIngredients.filter((i) => i !== ingredient))
                  }
                  className="ml-2 text-primary-600 hover:text-primary-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRecipes.map((recipe) => (
          <div
            key={recipe.id}
            onClick={() => setSelectedRecipe(recipe)}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
          >
            {recipe.imageUrl ? (
              <img
                src={recipe.imageUrl}
                alt={recipe.name}
                className="w-full h-48 object-cover"
              />
            ) : (
              <div className="w-full h-48 bg-primary-50 flex items-center justify-center">
                <Users className="h-12 w-12 text-primary-300" />
              </div>
            )}
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-3">{recipe.name}</h3>
              
              <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary-500" />
                  <span>{formatTime(recipe.prepTime + recipe.cookTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary-500" />
                  <span>{recipe.servings} servings</span>
                </div>
              </div>

              {recipe.tags && recipe.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl overflow-hidden relative">
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleVisibility(selectedRecipe);
                }}
                disabled={updatingVisibility}
                className="bg-white/90 hover:bg-white"
              >
                {selectedRecipe.isPublic ? (
                  <Globe className="h-5 w-5 text-primary-600" />
                ) : (
                  <Lock className="h-5 w-5" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareModal(true);
                }}
                className="bg-white/90 hover:bg-white"
              >
                <Send className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => handleExternalShare(selectedRecipe, e)}
                disabled={sharing}
                className="bg-white/90 hover:bg-white"
              >
                <Share2 className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedRecipe(null)}
                className="bg-white/90 hover:bg-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {selectedRecipe.imageUrl && (
              <div className="relative h-64">
                <img
                  src={selectedRecipe.imageUrl}
                  alt={selectedRecipe.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <h2 className="absolute bottom-4 left-4 right-4 text-2xl font-semibold text-white">
                  {selectedRecipe.name}
                </h2>
              </div>
            )}

            <div className="p-6">
              {!selectedRecipe.imageUrl && (
                <h2 className="text-2xl font-semibold mb-4">{selectedRecipe.name}</h2>
              )}

              <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary-500" />
                  <span>
                    Prep: {formatTime(selectedRecipe.prepTime)}
                    <span className="mx-1">•</span>
                    Cook: {formatTime(selectedRecipe.cookTime)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary-500" />
                  <span>{selectedRecipe.servings} servings</span>
                </div>
              </div>

              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-medium mb-3">Ingredients</h3>
                  <ul className="space-y-2">
                    {selectedRecipe.ingredients.map((ingredient, index) => (
                      <li key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                        <span className="w-16 text-sm text-gray-600">
                          {ingredient.amount} {ingredient.unit}
                        </span>
                        <span>{ingredient.name}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-3">Instructions</h3>
                  <ol className="space-y-4">
                    {selectedRecipe.instructions.map((instruction, index) => (
                      <li key={index} className="flex gap-4">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center text-sm">
                          {index + 1}
                        </span>
                        <span className="flex-1">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                {selectedRecipe.tags && selectedRecipe.tags.length > 0 && (
                  <section>
                    <h3 className="text-lg font-medium mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecipe.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Share Recipe</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowShareModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={searchUser}
                onChange={(e) => handleSearchUsers(e.target.value)}
                placeholder="Search users..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {searchResults.length > 0 && (
              <div className="mb-4 space-y-2">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`p-2 rounded-lg cursor-pointer flex items-center gap-3 ${
                      selectedUser?.id === user.id ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {user.username[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{user.username}</p>
                      {user.display_name && (
                        <p className="text-sm text-gray-500">{user.display_name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={() => handleSendRecipe(selectedRecipe, selectedUser?.user_id)}
              disabled={!selectedUser || sendingRecipe}
              className="w-full"
            >
              {sendingRecipe ? 'Sharing...' : 'Share Recipe'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}